import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {selectUser} from 'features/User'
import {useSelector} from 'react-redux'
import {format} from 'date-fns'

import {selectShadowITSearchQuery} from './analyticsShadowITSelectors'
import {
  ProductivitySearchInputState,
  ShadowITEntity,
  ShadowITFilter,
  TopActivityUser,
} from 'interfaces'
import {getAnalyticsSearchQueryParams} from '../helpers'
import {AnalyticsService} from 'services/api'
import {streamGraphColors, streamGraphPatterns} from './constants'

const QUERY_KEY = 'getShadowITStreamChartData'
export const useShadowITStreamChartQuery = () => {
  const {usersImported} = useSelector(selectUser)
  const shadowITSearchQuery = useSelector(selectShadowITSearchQuery)

  const {
    data: shadowITItemsByView = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: [
      QUERY_KEY,
      shadowITSearchQuery?.searchType,
      shadowITSearchQuery?.searchTerm,
    ],
    queryFn: () =>
      shadowITStreamChartQueryFn(shadowITSearchQuery as ShadowITFilter),
    select: ({data}) => data,
    enabled: usersImported,
  })

  const shadowITTopActivityData = useMemo(() => {
    if (!shadowITItemsByView?.length) {
      return []
    }
    /**
     * Parse the array and club the apps/location name
     * Add up the dataConsumed value for each app/location
     * Concat all users for each app/location and then filter unique email id's to avoid showing duplicate values
     */
    const topActivityData = shadowITItemsByView.reduce((acc, item) => {
      const dataToParse = item?.apps || []
      const dataItem = dataToParse.reduce((accX, entity) => {
        // Get users and concat it to previous users record
        const users: TopActivityUser[] = (
          accX[entity.name]?.users || []
        ).concat(entity?.users || [])
        // Filter unique users
        const uniqueUsers: TopActivityUser[] = [
          ...new Map(
            users.map((user: TopActivityUser) => [user.email, user]),
          ).values(),
        ]
        return {
          ...accX,
          [entity.name]: {
            ...(accX[entity.name] || {}),
            name: entity.name,
            dataConsumed:
              (accX[entity.name]?.dataConsumed || 0) + entity.dataConsumed,
            dataSent: (accX[entity.name]?.dataSent || 0) + entity.dataSent,
            users: uniqueUsers, // todo: review this and remove if not needed
            noOfUsers: uniqueUsers.length,
          },
        }
      }, acc)
      return {...dataItem}
    }, {})
    /**
     * The data object has all the values clubbed for respective app/location
     * However we need to return an array instead of data object as iterating over an object does not guarantee the sequence
     * So get the originalSequence, loop through it and get the final ordered clubbed array
     * Taking items[0] object as all the dates have same apps/location list
     */
    const originalSequence = shadowITItemsByView[0]?.apps || []

    // return the ordered clubbed sequence
    return (
      originalSequence?.map(
        (item: ShadowITEntity) => topActivityData[item.name],
      ) || []
    )
  }, [shadowITItemsByView])

  // 5 chart patterns used and repeated for top 20 apps/locations
  const shadowITStreamChartData = useMemo(() => {
    const chartData = shadowITItemsByView?.reduce((acc, item) => {
      const dataToParse = item.apps || []
      const dataItem = dataToParse.reduce((accX, entity, index) => {
        return {
          ...accX,
          [entity.name]: {
            ...(accX[entity.name] || {}),
            color: streamGraphColors[index % streamGraphColors.length],
            data: (accX[entity.name]?.data || []).concat({
              y: entity.dataConsumed,
              noOfUsers: entity.noOfUsers,
              app: entity.name,
              users: entity.users, // todo: review this and remove if not needed
            }),
            name: '',
            fillColor:
              index < streamGraphPatterns.length
                ? {
                    pattern: streamGraphPatterns[index],
                  }
                : undefined,
          },
        }
      }, acc)

      return {...dataItem}
    }, {})

    const days = shadowITItemsByView?.map(item =>
      format(new Date(item.date), 'eee'),
    )

    return {
      data: Object.values(chartData),
      days,
    }
  }, [shadowITItemsByView])

  return {
    isLoading,
    isError,
    shadowITStreamChartData,
    shadowITTopActivityData,
  }
}

export const shadowITStreamChartQueryFn = async (
  shadowITSearchQuery: ProductivitySearchInputState,
) => {
  try {
    const params = getAnalyticsSearchQueryParams(shadowITSearchQuery)
    const res = await AnalyticsService.getShadowIT({params})
    return res
  } catch (error) {
    // rejected promise with error
    return Promise.reject(error)
  }
}
